/*
 * Black November Flash Deals countdown row.
 *
 * Deadlines are absolute wall-clock times in Asia/Beirut, converted to real instants with the
 * browser's time-zone database, so the visitor's own time zone never matters. Nothing here is
 * relative to page load, so refreshing can never restart a timer.
 */
(() => {
  const DAY = 86400000;
  const formatters = new Map();

  const formatterFor = (zone) => {
    if (!formatters.has(zone)) {
      formatters.set(zone, new Intl.DateTimeFormat('en-US', {
        timeZone: zone, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }));
    }
    return formatters.get(zone);
  };

  // Offset (ms) of `zone` from UTC at the instant `epoch`.
  const zoneOffsetMs = (epoch, zone, fallbackMinutes) => {
    try {
      const parts = {};
      for (const part of formatterFor(zone).formatToParts(new Date(epoch))) parts[part.type] = part.value;
      const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
      return asUtc - Math.floor(epoch / 1000) * 1000;
    } catch (error) {
      return fallbackMinutes * 60000;
    }
  };

  // 'YYYY-MM-DDTHH:MM:SS' written in `zone` -> epoch ms.
  const wallToEpoch = (wall, zone, fallbackMinutes) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(wall);
    if (!m) return NaN;
    const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    let epoch = guess - zoneOffsetMs(guess, zone, fallbackMinutes);
    epoch = guess - zoneOffsetMs(epoch, zone, fallbackMinutes); // second pass settles DST edges
    return epoch;
  };

  const nextDayMidnight = (wall) => {
    const d = new Date(Date.UTC(+wall.slice(0, 4), +wall.slice(5, 7) - 1, +wall.slice(8, 10) + 1));
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T00:00:00`;
  };

  const parseRounds = (list, zone, fallbackMinutes) => list
    .map((round) => ({
      id: round.id,
      final: Boolean(round.final),
      live: round.live === true,
      start: wallToEpoch(round.start, zone, fallbackMinutes),
      // The timer reaches zero at 11:59:59 PM; the round window runs to the next midnight.
      expires: wallToEpoch(round.expires, zone, fallbackMinutes),
      endExclusive: wallToEpoch(nextDayMidnight(round.expires), zone, fallbackMinutes)
    }))
    .filter((round) => Number.isFinite(round.start) && Number.isFinite(round.expires) && Number.isFinite(round.endExclusive))
    .sort((a, b) => a.start - b.start);

  // state: 'upcoming' (nothing yet), 'active' (live timer), 'ended' (no flash deals running).
  const resolve = (rounds, now, dealsAvailable) => {
    if (!rounds.length || now < rounds[0].start) return { state: 'upcoming', round: null };
    const round = rounds.find((r) => now >= r.start && now < r.endExclusive);
    if (!round) return { state: 'ended', round: null };
    if (!round.live || !dealsAvailable) return { state: 'ended', round };
    return { state: 'active', round, remaining: Math.max(0, Math.ceil((round.expires - now) / 1000)) };
  };

  const breakdown = (seconds) => [
    Math.floor(seconds / 86400),
    Math.floor(seconds / 3600) % 24,
    Math.floor(seconds / 60) % 60,
    seconds % 60
  ];

  const core = { wallToEpoch, parseRounds, resolve, breakdown };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;

  class FTCountdown extends HTMLElement {
    connectedCallback() {
      const data = this.dataset;
      this.zone = data.timezone || 'Asia/Beirut';
      this.fallback = Number(data.fallbackOffset || 120);
      let list = [];
      try { list = JSON.parse(data.rounds || '[]'); } catch (error) { list = []; }
      this.rounds = parseRounds(list, this.zone, this.fallback);
      this.dealsAvailable = data.deals === 'true';
      this.skew = 0;
      this.units = ['days', 'hours', 'minutes', 'seconds'].map((unit) => this.querySelector(`[data-unit="${unit}"]`));
      this.clock = this.querySelector('[data-bn-clock]');
      this.lastLabelMinute = -1;
      this.onVisible = () => { if (!document.hidden) this.render(); };
      document.addEventListener('visibilitychange', this.onVisible);
      this.render();
      this.interval = setInterval(() => this.render(), 250);
      this.syncClock();
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    now() { return Date.now() + this.skew; }

    // A wrong device clock would show a wrong countdown, so compare it with the server's clock once.
    async syncClock() {
      try {
        const started = performance.now();
        const response = await fetch('/', { method: 'HEAD', cache: 'no-store', credentials: 'omit' });
        const header = response.headers.get('date');
        const server = header ? Date.parse(header) : NaN;
        if (!Number.isFinite(server)) return;
        const skew = server + 500 + (performance.now() - started) / 2 - Date.now();
        // The Date header only has 1-second resolution: ignore small differences and absurd ones.
        if (Math.abs(skew) > 3000 && Math.abs(skew) < 7 * DAY) {
          this.skew = skew;
          this.render();
        }
      } catch (error) { /* keep the device clock */ }
    }

    render() {
      const result = resolve(this.rounds, this.now(), this.dealsAvailable);
      if (this.dataset.bnState !== result.state) this.dataset.bnState = result.state;

      if (result.state !== 'active') return;

      const values = breakdown(result.remaining);
      this.units.forEach((node, index) => {
        const next = String(values[index]).padStart(2, '0');
        if (node.textContent !== next) node.textContent = next;
      });
      const minuteKey = Math.floor(result.remaining / 60);
      if (minuteKey !== this.lastLabelMinute) {
        this.lastLabelMinute = minuteKey;
        this.clock.setAttribute('aria-label', `${values[0]} days, ${values[1]} hours, ${values[2]} minutes remaining`);
      }
    }
  }

  if (!customElements.get('ft-countdown')) customElements.define('ft-countdown', FTCountdown);
})();
